/**
 * Fixed Asia/Singapore boundaries for infrastructure ledgers, attendance and
 * legacy data. Student habits use practiceDayService and its API day window;
 * do not reuse this policy for streaks, daily tiles or receipt filtering.
 */

export const SINGAPORE_UTC_OFFSET_MS = 8 * 60 * 60 * 1000;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Start of the Asia/Singapore calendar day containing `d`, expressed as a
 * UTC instant. Works by shifting into SGT, zeroing to midnight, then
 * shifting back.
 */
export function startOfDaySGT(d: Date): Date {
  const shifted = new Date(d.getTime() + SINGAPORE_UTC_OFFSET_MS);
  const sgtMidnightMs = Date.UTC(
    shifted.getUTCFullYear(),
    shifted.getUTCMonth(),
    shifted.getUTCDate()
  );
  return new Date(sgtMidnightMs - SINGAPORE_UTC_OFFSET_MS);
}

/**
 * Start of the SGT calendar day *after* the one containing `d`, as a UTC
 * instant. This is the exclusive upper bound for "today in SGT" range
 * queries — pair it with `startOfDaySGT` as
 * `{ $gte: startOfDaySGT(now), $lt: startOfNextDaySGT(now) }`. SG has no DST,
 * so the next midnight is always exactly +24h.
 */
export function startOfNextDaySGT(d: Date): Date {
  return new Date(startOfDaySGT(d).getTime() + MS_PER_DAY);
}

/** Whole-day difference (SGT) between two dates, a - b. */
export function dayDiffSGT(a: Date, b: Date): number {
  return Math.round(
    (startOfDaySGT(a).getTime() - startOfDaySGT(b).getTime()) / MS_PER_DAY
  );
}

/**
 * `YYYY-MM-DD` key for the SGT calendar day containing `d`. Used as the
 * dedupe key for once-per-day client UI moments (confetti, modals) so that
 * the rollover matches the server's day boundary.
 */
export function sgtDateKey(d: Date = new Date()): string {
  const shifted = new Date(d.getTime() + SINGAPORE_UTC_OFFSET_MS);
  const y = shifted.getUTCFullYear();
  const m = String(shifted.getUTCMonth() + 1).padStart(2, '0');
  const day = String(shifted.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
